import { useState } from "react";
import type { SyntheticEvent } from "react";
import { ArrowUp, Sparkles } from "lucide-react";
import { useNavigate } from "react-router";
import { saveDraftPrompt } from "../project-intake.js";

const quickStarts = [
  "Plan new flooring",
  "Help me measure a room",
  "Compare material options",
] as const;

export function LandingChatPrompt({ dark = false }: { dark?: boolean }) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");

  const start = (event?: SyntheticEvent<HTMLFormElement>, quickStart?: string) => {
    event?.preventDefault();
    const prompt = (quickStart ?? input).trim();
    if (!prompt) return;
    saveDraftPrompt(prompt);
    void navigate("/chat");
  };

  return (
    <div className="mt-8 w-full">
      <form onSubmit={(event) => start(event)}>
        <div
          className={`rounded-[1.65rem] border p-2.5 shadow-[0_18px_55px_rgba(0,0,0,0.22)] transition ${dark ? "border-white/15 bg-white/10 focus-within:border-orange-400 focus-within:ring-4 focus-within:ring-orange-500/15" : "border-stone-300 bg-white focus-within:border-emerald-700 focus-within:ring-4 focus-within:ring-emerald-100"}`}
        >
          <label className="sr-only" htmlFor="landing-project-prompt">
            Describe your project
          </label>
          <textarea
            autoFocus
            className={`block min-h-24 w-full resize-none bg-transparent px-3 py-3 text-base leading-7 outline-none sm:min-h-28 sm:text-lg ${dark ? "text-white placeholder:text-stone-500" : "text-stone-800 placeholder:text-stone-400"}`}
            id="landing-project-prompt"
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder="Describe your project, what you want to change, or where you need help…"
            rows={2}
            value={input}
          />
          <div
            className={`flex items-center justify-between gap-3 border-t px-2 pt-2 ${dark ? "border-white/10" : "border-stone-100"}`}
          >
            <p
              className={`flex items-center gap-2 text-xs font-semibold ${dark ? "text-stone-400" : "text-stone-500"}`}
            >
              <Sparkles className="text-orange-600" size={15} /> Start naturally
            </p>
            <button
              aria-label="Start project conversation"
              className={`grid h-11 w-11 shrink-0 place-items-center rounded-full transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-35 ${dark ? "bg-orange-500 text-white hover:bg-orange-400" : "bg-emerald-900 text-white hover:bg-emerald-950"}`}
              disabled={!input.trim()}
              type="submit"
            >
              <ArrowUp size={20} />
            </button>
          </div>
        </div>
      </form>
      <div className="mt-3 flex flex-wrap gap-2">
        {quickStarts.map((label) => (
          <button
            className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition ${dark ? "border-white/20 bg-white/5 text-stone-300 hover:border-orange-400 hover:bg-orange-500/10 hover:text-white" : "border-stone-300 bg-white/80 text-stone-600 hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-900"}`}
            key={label}
            onClick={() => start(undefined, label)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs leading-5 text-stone-500">
        Next, choose the contractor whose services and pricing should guide the conversation.
      </p>
    </div>
  );
}
